/**
 * Date utility functions for consistent timezone handling
 * All dates are displayed in Eastern Time (ET - America/New_York)
 */

/**
 * Format date to EST/EDT with full date and time
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string in EST/EDT
 */
export const formatDateTimeEST = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Format date to EST/EDT with date only (no time)
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string in EST/EDT
 */
export const formatDateEST = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Format date to EST/EDT with full date (long format)
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string in EST/EDT
 */
export const formatDateLongEST = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Format date to EST/EDT with short date format
 * @param dateString - ISO date string or Date object
 * @returns Formatted date string in EST/EDT (MM/DD/YYYY)
 */
export const formatDateShortEST = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Convert a UTC date string from the DB to a datetime-local input value in ET.
 * datetime-local expects "YYYY-MM-DDTHH:mm" with no timezone suffix.
 */
export const utcToETInputValue = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  // Format as YYYY-MM-DDTHH:mm in America/New_York
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

/**
 * Convert a datetime-local input value (treated as ET) to a UTC ISO string for the backend.
 */
export const etInputValueToUTC = (localValue: string): string => {
  if (!localValue) return '';
  // Append ET offset so Date parses it correctly.
  // Use Intl to find the current ET offset for the given date.
  const naive = new Date(localValue); // parsed as local browser time
  if (isNaN(naive.getTime())) return '';
  // Re-interpret the wall-clock value as ET by formatting a UTC epoch back
  // through the ET timezone until we find the matching UTC time.
  const etFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  });
  // Binary-search isn't needed — we can use the offset trick:
  // Get ET offset by comparing a known UTC time to its ET representation.
  const tempDate = new Date(localValue + 'Z'); // treat as UTC first to get a ballpark
  const etParts = etFormatter.formatToParts(tempDate);
  const get = (type: string) => etParts.find(p => p.type === type)?.value ?? '00';
  const etWall = `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
  // Offset in ms between what ET shows and what we want
  const offsetMs = new Date(etWall + 'Z').getTime() - tempDate.getTime();
  return new Date(tempDate.getTime() - offsetMs).toISOString();
};
